import { ref, watch, computed } from 'vue'
import type { PageContext } from 'vike/types'
import { usePageContext } from 'vike-vue/usePageContext'
import { navigate } from 'vike/client/router'
import qs from 'qs'

import type { Filters } from '@repo/types'
import type { PageDataBase } from '../hooks'

function debounce(fn: () => void, ms = 1000) {
  let timer: ReturnType<typeof setTimeout> | undefined
  return () => {
    clearTimeout(timer)
    timer = setTimeout(fn, ms)
  }
}

type View = 'grid' | 'map' | 'list'

export function useSearchQuery() {
  const pageContext = usePageContext() as PageContext<PageDataBase & {
    filters: Filters
  }>;

  // -- Initial state from URL --
  // pageContext.search unreliable + bracket array expression for homeType
  const query = qs.parse(pageContext.urlOriginal.split('?')[1] || '');

  const filters = ref<Filters>(pageContext.data?.filters || {
    homeType: [],
    price: { min: 0, max: undefined },
    size: { min: 0, max: undefined },
    labels: []
  });

  const viewMode = ref<View>(['grid', 'map', 'list'].includes(query.view as string) ? query.view as View : 'map')
  const currentPage = ref(1)

  watch(filters, () => {
    currentPage.value = 1
  })

  if (query.page) {
    currentPage.value = parseInt(query.page as string)
  }
  if (query.view && typeof query.view === 'string') {
    const s = (query as unknown as { view: string }).view
    const view: View = ['grid', 'list', 'map'].includes(s) ? s as View : 'map'
    viewMode.value = view
  }

  // -- URL updates --

  const queryParams = computed(() => {
    const params: Record<string, string | number | string[]> = {}

    // Filters
    if (filters.value.homeType.length) {
      params.homeType = filters.value.homeType
    }
    if (filters.value.labels.length) {
      params.labels = filters.value.labels
    }
    if (filters.value.price.min > 0 || filters.value.price.max) {
      params.price = `${filters.value.price.min || 0}-${filters.value.price.max || ''}`
    }
    if (filters.value.size.min > 0 || filters.value.size.max) {
      params.size = `${filters.value.size.min || 0}-${filters.value.size.max || ''}`
    }

    // Pagination
    if (currentPage.value > 1) {
      params.page = currentPage.value
    }

    // View mode
    if (viewMode.value !== 'map') {
      params.view = viewMode.value
    }

    return params
  })

  const updateUrl = debounce(() => {
    const queryString = qs.stringify(queryParams.value)
    const { path } = pageContext.routeParams
    const baseUrl = path ? `/${path}/search` : '/buy/search'

    // an empty query string should not have a question mark
    const url = queryString ? `${baseUrl}?${queryString}` : baseUrl

    navigate(url, { keepScrollPosition: true, overwriteLastHistoryEntry: true })
  }, 500)

  watch([filters, currentPage, viewMode], updateUrl, { deep: true, immediate: false })

  return {
    filters,
    currentPage,
    viewMode
  }
}
